import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class BatchTemplateHeader extends ModelObjectWithDirtyCheck<BatchTemplateHeader> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Name.
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    private _materialProducedStockItemId: Guid | null = null;
    /**
    * Stock Item Id produced by the bathc template.
    */
    public get materialProducedStockItemId(): Guid | null { return this._materialProducedStockItemId }
    public set materialProducedStockItemId(value: Guid | null) {
      this._materialProducedStockItemId = value === undefined ? null : value;
    }
    private _producedQuantity: number | null = null;
    /**
    * Quantity of output item which is produced by Template (null if no output defined). Used for scaling template at batch creation.
    * Not Negative
    */
    public get producedQuantity(): number | null { return this._producedQuantity }
    public set producedQuantity(value: number | null) {
      this._producedQuantity = value === undefined ? null : value;
    }
    public isHidden: boolean = false;
    /**
    * Vessel Fill Colour
    * Not Nullable
    */
    public vesselFillColour: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.materialProducedStockItemId = jsonSerializedObject.materialProducedStockItemId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.materialProducedStockItemId);
          this.producedQuantity = jsonSerializedObject.producedQuantity == null ? null : jsonSerializedObject.producedQuantity;
          this.isHidden = jsonSerializedObject.isHidden;
          this.vesselFillColour = jsonSerializedObject.vesselFillColour;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.materialProducedStockItemId = this.materialProducedStockItemId == null ? null : this.materialProducedStockItemId.toString();
        toRet.producedQuantity = this.producedQuantity == null ? null : this.producedQuantity;
        toRet.isHidden = this.isHidden;
        toRet.vesselFillColour = this.vesselFillColour;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "BatchTemplateHeader.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "materialProducedStockItemId", rule: (v: any) => v == null || !v.isEmpty() || "BatchTemplateHeader.MaterialProducedStockItemId cannot be empty"},
    ];
}


export class BatchTemplateRecipe extends ModelObject<BatchTemplateRecipe> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Id of the Batch Template where the recipe belongs to.
    */
    public batchTemplateId: Guid = Guid.createEmpty();
    /**
    * Id of the Recipe.
    */
    public recipeId: Guid = Guid.createEmpty();
    /**
    * Will be used for scaling the recipe when used in a process.
    * Greater Than : 0
    */
    public scale: number = 0;
    public prerequisites: (Guid)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.batchTemplateId = Guid.parseOrEmpty(jsonSerializedObject.batchTemplateId);
          this.recipeId = Guid.parseOrEmpty(jsonSerializedObject.recipeId);
          this.scale = jsonSerializedObject.scale;
          this.prerequisites = jsonSerializedObject.prerequisites.map((v: any) => Guid.parseOrEmpty(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.batchTemplateId = this.batchTemplateId.toString();
        toRet.recipeId = this.recipeId.toString();
        toRet.scale = this.scale;
        toRet.prerequisites = this.prerequisites.map(v => v.toString());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "BatchTemplateRecipe.Id cannot be empty"},
            {field: "batchTemplateId", rule: (v: any) => !v.isEmpty() || "BatchTemplateRecipe.BatchTemplateId cannot be empty"},
            {field: "recipeId", rule: (v: any) => !v.isEmpty() || "BatchTemplateRecipe.RecipeId cannot be empty"},
            {field: "scale", rule: (v: any) => v == null || v > 0 || "Scale is not greater than minimum allowed value: 0"},
    ];
}


export class BatchTemplate extends ModelObject<BatchTemplate> {

    public header: BatchTemplateHeader = new BatchTemplateHeader();
    public recipes: (BatchTemplateRecipe)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new BatchTemplateHeader(jsonSerializedObject.header);
          this.recipes = jsonSerializedObject.recipes.map((v: any) => new BatchTemplateRecipe(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.recipes = this.recipes.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "recipes", rule: (v: any) => !!v || "recipes is required"},
            {field: "recipes", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as BatchTemplateRecipe[]).reduce((acc: Array<string>, curr: BatchTemplateRecipe) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class BatchTemplateService extends ServiceBase {

  public async getBatchTemplate(batchTemplateId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<BatchTemplate> {
    const req = {
    tenantId:api.tenantId.toString(),
    batchTemplateId:batchTemplateId.toString(),
    };
    const extractor = (response:any) => new BatchTemplate(response.batchTemplate);;
    return this.makeJsonRequest("batchTemplate/v1/getBatchTemplate", req, false, extractor, opts);
  };

  public async getAllBatchTemplates(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(BatchTemplate)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.batchTemplates.map((v: any) => new BatchTemplate(v));;
    return this.makeJsonRequest("batchTemplate/v1/getAllBatchTemplates", req, false, extractor, opts);
  };

  public async setBatchTemplateIsHidden(batchTemplateId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    batchTemplateId:batchTemplateId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("batchTemplate/v1/setBatchTemplateIsHidden", req, true, extractor, opts);
  };

  public async saveBatchTemplateHeader(header: BatchTemplateHeader, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    header:header.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("batchTemplate/v1/saveBatchTemplateHeader", req, true, extractor, opts);
  };

  public async saveBatchTemplateRecipes(recipes: readonly BatchTemplateRecipe[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    recipes:recipes.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("batchTemplate/v1/saveBatchTemplateRecipes", req, true, extractor, opts);
  };

  public async hardDeleteBatchTemplateRecipe(batchTemplateRecipeId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    batchTemplateRecipeId:batchTemplateRecipeId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("batchTemplate/v1/hardDeleteBatchTemplateRecipe", req, true, extractor, opts);
  };
}

export const batchTemplateService = new BatchTemplateService();